import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { expValEquals } from '@confluence/feature-experiments';

import CommentIcon from '@atlaskit/icon/core/comment';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import type { Identifier } from '@atlaskit/media-client/identifier';
import type { MediaNodeContext } from '@atlaskit/renderer/actions';

import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

import { getOccurrenceKey, occurrenceKeysMatch } from '../utils/mediaOccurrenceKey.ts';

import {
	getMediaViewerSidebarState,
	setMediaViewerOpenFromIdentifier,
	setMediaViewerSelectedIdentifier,
	setMediaViewerSidebarOpen,
} from './mediaViewerSidebarStore.ts';

export type MediaTargetResolution =
	| { status: 'resolved'; target: MediaNodeContext }
	| { status: 'missing' | 'ambiguous' | 'unsupported' };

export type MediaViewerCommentSidebarProps = {
	resolveTarget: (identifier?: Identifier) => MediaTargetResolution;
	keepLastResolvedTarget?: boolean;
	selectedIdentifier?: Identifier;
};

export type MediaViewerCommentSidebarComponent =
	React.ComponentType<MediaViewerCommentSidebarProps>;

const isFileIdentifier = (identifier?: Identifier): identifier is Identifier & { id: string } =>
	!!identifier && 'id' in identifier && typeof identifier.id === 'string';

const i18n = defineMessages({
	hideComments: {
		id: 'editor-features.media-viewer.sidebar.hide',
		defaultMessage: 'Hide comments',
		description: 'Accessible label for the button that hides the Media Viewer comments sidebar.',
	},
	showComments: {
		id: 'editor-features.media-viewer.sidebar.show',
		defaultMessage: 'Show comments',
		description: 'Accessible label for the button that shows the Media Viewer comments sidebar.',
	},
	commentsTitle: {
		id: 'editor-features.media-viewer.sidebar.title',
		defaultMessage: 'Comments',
		description: 'Title for the Media Viewer comments sidebar.',
	},
});

// Resolves the media viewer's selected identifier to the media node it is showing, so the
// Media Viewer comment sidebar can read that node's annotations. Only the view-page branch is
// implemented; editor/live resolution uses the media plugin's mounted nodes instead and arrives
// with the editor/live change.
export function useResolveMediaTarget({
	isViewPage,
}: {
	isViewPage: boolean;
}): (identifier?: Identifier) => MediaTargetResolution {
	const { rendererActions } = useRendererActions();

	const resolveMediaTarget = useCallback(
		(identifier?: Identifier) => {
			if (!isFileIdentifier(identifier)) {
				return { status: 'unsupported' as const };
			}
			if (!isViewPage) {
				return { status: 'unsupported' as const };
			}

			const result = rendererActions?.getMediaNodeContext({
				id: identifier.id,
				occurrenceKey: getOccurrenceKey(identifier),
			});
			if (!result || result.status !== 'resolved') {
				return result ?? { status: 'missing' as const };
			}
			return { status: 'resolved' as const, target: result.context };
		},
		[isViewPage, rendererActions],
	);

	return resolveMediaTarget;
}

// Builds the media viewer comments sidebar configuration and the persistent viewer state
// callbacks the Media Viewer uses to survive renderer remounts. The sidebar component is passed in
// because @confluence/comments-panel depends back on this package. Returns undefined unless comments
// are enabled for this surface and a sidebar is supplied.
//
// IMPORTANT: pass the returned object down to Media Viewer as-is. Some of its fields are getters that
// read the viewer state when Media Viewer reads them; copying or merging the object
// (e.g. `{ ...extensions }`) reads them once and freezes those values.
// Re-rendering this triggers ADFRenderer / FullPageEditorComponent re-renders, so be judicious
export function useMediaCommentExtensions({
	isViewPage,
	isEnabled,
	CommentSidebar,
}: {
	isViewPage: boolean;
	isEnabled: boolean;
	CommentSidebar?: MediaViewerCommentSidebarComponent;
}): MediaViewerExtensions | undefined {
	const { formatMessage } = useIntl();
	const resolveMediaTarget = useResolveMediaTarget({ isViewPage });

	const renderMediaCommentSidebar = useCallback(
		(selectedIdentifier: Identifier) => {
			if (!CommentSidebar) {
				return null;
			}
			return (
				<CommentSidebar
					resolveTarget={resolveMediaTarget}
					keepLastResolvedTarget={!isViewPage}
					selectedIdentifier={selectedIdentifier}
				/>
			);
		},
		[CommentSidebar, isViewPage, resolveMediaTarget],
	);

	return useMemo((): MediaViewerExtensions | undefined => {
		if (
			!isEnabled ||
			!CommentSidebar ||
			!expValEquals('cc_comments_media_viewer_sidebar', 'isEnabled', true)
		) {
			return undefined;
		}

		return {
			// Persist preview owner, selected preview item, and sidebar open/closed
			// across MediaViewer remounts (e.g. when the renderer re-mounts after
			// `updateDocumentFn` writes a new doc after media-comment save/delete).
			// Confluence owns the matching policy: the card opened from a file id hosts
			// the modal, while selectedIdentifier tracks the currently viewed item.
			getMediaViewerSelectedItem: (identifier) => {
				const { viewerOpenFromIdentifier, selectedIdentifier } = getMediaViewerSidebarState();
				if (
					!viewerOpenFromIdentifier ||
					!('id' in identifier) ||
					!('id' in viewerOpenFromIdentifier)
				) {
					return null;
				}
				return identifier.id === viewerOpenFromIdentifier.id &&
					occurrenceKeysMatch(
						getOccurrenceKey(identifier),
						getOccurrenceKey(viewerOpenFromIdentifier),
					)
					? (selectedIdentifier ?? identifier)
					: null;
			},
			onSelectedItemChange: (identifier) => {
				const { viewerOpenFromIdentifier } = getMediaViewerSidebarState();
				setMediaViewerSelectedIdentifier(identifier);
				if (!identifier || !viewerOpenFromIdentifier) {
					setMediaViewerOpenFromIdentifier(identifier);
				}
			},
			// Getters, so MediaViewer reads the current store value when it mounts or re-renders its
			// header, without this hook subscribing to the store and re-rendering the page on every change.
			get defaultSidebarVisible() {
				const { isSidebarOpen } = getMediaViewerSidebarState();
				return isSidebarOpen;
			},
			onSidebarVisibilityChange: setMediaViewerSidebarOpen,
			sidebar: {
				icon: <CommentIcon label="" spacing="spacious" />,
				get label() {
					const { isSidebarOpen } = getMediaViewerSidebarState();
					return formatMessage(isSidebarOpen ? i18n.hideComments : i18n.showComments);
				},
				title: formatMessage(i18n.commentsTitle),
				renderer: renderMediaCommentSidebar,
			},
		};
	}, [isEnabled, CommentSidebar, renderMediaCommentSidebar, formatMessage]);
}
